import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { Tabs, type TabItem } from "@/components/Tabs";
import { SearchableMultiSelect } from "@/components/SearchableMultiSelect";
import type { MultiSelectOption } from "@/components/SearchableMultiSelect";
import type { AiUsageFilters } from "@/pages/AiConsumption/types";

interface AiConsumptionFiltersProps {
    filters: AiUsageFilters;
    onFilterChange: (filters: AiUsageFilters) => void;
    users: Array<{ id: number; name: string }>;
    providers: string[];
    models: string[];
    contextTypes: string[];
}

export const AiConsumptionFilters: React.FC<AiConsumptionFiltersProps> = ({
    filters,
    onFilterChange,
    users,
    providers,
    models,
    contextTypes,
}) => {
    const { t } = useTranslation("global");

    const periods: TabItem<AiUsageFilters["period"]>[] = [
        { id: "all", label: t("ai_consumption.period_all") },
        { id: "today", label: t("ai_consumption.period_today") },
        { id: "week", label: t("ai_consumption.period_week") },
        { id: "month", label: t("ai_consumption.period_month") },
    ];

    // Build MultiSelectOption arrays (id must be numeric)
    const userOptions: MultiSelectOption[] = users.map((u) => ({
        id: u.id,
        name: u.name,
    }));

    const providerOptions: MultiSelectOption[] = providers.map((p, i) => ({
        id: i + 1,
        name: p,
    }));

    const modelOptions: MultiSelectOption[] = models.map((m, i) => ({
        id: i + 1,
        name: m,
    }));

    const contextOptions: MultiSelectOption[] = contextTypes.map((c, i) => {
        const key = `ai_consumption.context_types.${c}`;
        const label = t(key);
        return { id: i + 1, name: label && label !== key ? label : c };
    });

    // Convert string[] values to number[] for SearchableMultiSelect
    const selectedUserIds = filters.user_ids.map(Number).filter(Boolean);

    const selectedProviderIds = filters.providers
        .map((p) => {
            const opt = providerOptions.find((o) => o.name === p);
            return opt ? opt.id : 0;
        })
        .filter(Boolean);

    const selectedModelIds = filters.models
        .map((m) => {
            const opt = modelOptions.find((o) => o.name === m);
            return opt ? opt.id : 0;
        })
        .filter(Boolean);

    const selectedContextIds = filters.context_types
        .map((c) => {
            const key = `ai_consumption.context_types.${c}`;
            const label = t(key);
            const name = label && label !== key ? label : c;
            const opt = contextOptions.find((o) => o.name === name);
            return opt ? opt.id : 0;
        })
        .filter(Boolean);

    const hasActiveSelectFilters = Boolean(
        filters.user_ids.length > 0 ||
            filters.providers.length > 0 ||
            filters.models.length > 0 ||
            filters.context_types.length > 0
    );

    const handleReset = (): void => {
        onFilterChange({
            ...filters,
            user_ids: [],
            providers: [],
            models: [],
            context_types: [],
        });
    };

    const handleUsersChange = (ids: number[]): void => {
        onFilterChange({ ...filters, user_ids: ids.map(String) });
    };

    const handleProvidersChange = (ids: number[]): void => {
        const names = ids
            .map((id) => providerOptions.find((o) => o.id === id)?.name ?? "")
            .filter(Boolean);
        onFilterChange({ ...filters, providers: names });
    };

    const handleModelsChange = (ids: number[]): void => {
        const names = ids
            .map((id) => modelOptions.find((o) => o.id === id)?.name ?? "")
            .filter(Boolean);
        onFilterChange({ ...filters, models: names });
    };

    const handleContextsChange = (ids: number[]): void => {
        const names = ids
            .map((id) => {
                const opt = contextOptions.find((o) => o.id === id);
                if (!opt) return "";
                // Reverse-map label back to context_type key
                const idx = contextOptions.indexOf(opt);
                return contextTypes[idx] ?? "";
            })
            .filter(Boolean);
        onFilterChange({ ...filters, context_types: names });
    };

    return (
        <div>
            {/* Period Tabs */}
            <div className="flex justify-center">
                <Tabs<AiUsageFilters["period"]>
                    activeTab={filters.period}
                    onChange={(tabId) =>
                        onFilterChange({ ...filters, period: tabId })
                    }
                    size="sm"
                    color="white"
                    syncUrl={false}
                    items={periods}
                    fit
                />
            </div>
            <div className="bg-box rounded-2xl border border-box p-4 shadow-xs space-y-4 w-full mt-4">
                {/* Multi-select filters */}
                <div className="space-y-4">
                    <div>
                        <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider mb-1">
                            {t("ai_consumption.filter_user")}
                        </label>
                        <SearchableMultiSelect
                            options={userOptions}
                            value={selectedUserIds}
                            onChange={handleUsersChange}
                            placeholder={t("ai_consumption.filter_all_users")}
                            hideFullscreen
                            clearable={false}
                            size="sm"
                            className="!mb-0"
                        />
                    </div>

                    <div>
                        <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider mb-1">
                            {t("ai_consumption.filter_provider")}
                        </label>
                        <SearchableMultiSelect
                            options={providerOptions}
                            value={selectedProviderIds}
                            onChange={handleProvidersChange}
                            placeholder={t(
                                "ai_consumption.filter_all_providers"
                            )}
                            hideFullscreen
                            clearable={false}
                            size="sm"
                            className="!mb-0"
                        />
                    </div>

                    <div>
                        <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider mb-1">
                            {t("ai_consumption.filter_model")}
                        </label>
                        <SearchableMultiSelect
                            options={modelOptions}
                            value={selectedModelIds}
                            onChange={handleModelsChange}
                            placeholder={t("ai_consumption.filter_all_models")}
                            hideFullscreen
                            clearable={false}
                            size="sm"
                            className="!mb-0"
                        />
                    </div>

                    <div>
                        <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider mb-1">
                            {t("ai_consumption.filter_context")}
                        </label>
                        <SearchableMultiSelect
                            options={contextOptions}
                            value={selectedContextIds}
                            onChange={handleContextsChange}
                            placeholder={t(
                                "ai_consumption.filter_all_contexts"
                            )}
                            hideFullscreen
                            clearable={false}
                            size="sm"
                            className="!mb-0"
                        />
                    </div>
                </div>
                {hasActiveSelectFilters && (
                    <div className="flex justify-center mb-1">
                        <button
                            type="button"
                            onClick={handleReset}
                            className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-rose-600 transition-colors px-3 py-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/20 cursor-pointer border border-transparent hover:border-rose-200 dark:hover:border-rose-900/30"
                        >
                            <Icon value="x" size={13} />
                            <span>{t("ai_consumption.reset_filters")}</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
